import type { SubmissionListItem } from '@jfh/intake-shared';
import { Avatar, EmptyState } from '../components/Primitives.js';
import { ApplicationPill, ProblemPill } from './badges.js';
import { timeAgo } from './fieldDisplay.js';
import { applicationType } from './wording.js';
import type { ListGroup } from './views.js';

/**
 * One application in the list (F10 P2): name, when it came in, its type, one
 * status pill and at most one problem. The reference, email and iQ code are
 * in the detail header.
 */
function Row({
  sub,
  active,
  onClick,
}: {
  sub: SubmissionListItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      style={{
        display: 'flex',
        width: '100%',
        textAlign: 'left',
        alignItems: 'center',
        gap: 11,
        padding: '11px 14px',
        background: active ? 'var(--accent-soft)' : 'transparent',
        border: 'none',
        borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
        borderBottom: '1px solid var(--border)',
        cursor: 'pointer',
        transition: 'background .12s',
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'var(--bg-surface-2)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <Avatar name={sub.applicant} size={32} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span
            style={{
              fontWeight: 700,
              fontSize: 13.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
            }}
          >
            {sub.applicant}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap', flex: 'none' }}>
            {timeAgo(sub.createdAt)}
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>{applicationType(sub.type)}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
          <ApplicationPill sub={sub} />
          <ProblemPill
            sub={sub}
            title={sub.iq && !sub.iq.ok ? (sub.iq.message ?? undefined) : (sub.teamsCardError ?? undefined)}
          />
        </div>
      </div>
    </button>
  );
}

export function SubmissionList({
  groups,
  selectedId,
  onSelect,
  empty,
}: {
  groups: ListGroup[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  empty: { title: string; body: string };
}) {
  if (!groups.some((g) => g.items.length)) {
    return <EmptyState icon="check" title={empty.title} body={empty.body} />;
  }
  return (
    <>
      {groups.map((g) => (
        <section key={g.key} aria-label={g.label ?? undefined}>
          {g.label && (
            <div
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                background: 'var(--bg-surface-2)',
                borderBottom: '1px solid var(--border)',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: g.key === 'fix' ? 'var(--st-declined)' : 'var(--jfh-cactus)',
              }}
            >
              {g.label}
              <span className="tnum" style={{ color: 'var(--text-3)', fontWeight: 700 }}>
                {g.items.length}
              </span>
            </div>
          )}
          {g.items.map((s) => (
            <Row key={s.id} sub={s} active={s.id === selectedId} onClick={() => onSelect(s.id)} />
          ))}
        </section>
      ))}
    </>
  );
}
